// DaysTogetherCounter — Hero Section Segment 1 (W3.1 §5.6).
//
// Renders the 「交往天數」 big text + meta line. Source: pair.anniversary_date
// (passed in by HeroSection). Animates 0 → N on mount via count-up.
// Respects prefers-reduced-motion.

import { differenceInCalendarDays } from "date-fns";
import { HeartHandshake } from "lucide-react";

import {
  daysTogetherLabel,
  anniversaryNthLabel,
  anchorMetaLabel,
} from "@/lib/anniversary/anniversaryLabel";
import { useCountUp, usePrefersReducedMotion } from "@/lib/anniversary/countUp";
import { parseDateOnly } from "@/lib/utils/date";

export interface DaysTogetherCounterProps {
  /** ISO date (YYYY-MM-DD) when the pair got together (anchor). */
  anchorIso: string | null | undefined;
  /** Today's date (HKT, defaults to `new Date()`). */
  now?: Date;
}

export function DaysTogetherCounter({ anchorIso, now }: DaysTogetherCounterProps) {
  const reduced = usePrefersReducedMotion();
  const target = anchorIso
    ? Math.max(0, differenceInCalendarDays(now ?? new Date(), parseDateOnly(anchorIso)))
    : 0;
  const displayed = useCountUp(target, { reduced });

  if (!anchorIso) {
    // No anchor — render null so the hero grid collapses cleanly.
    return null;
  }

  const metaLabel = anchorMetaLabel(anchorIso, now);
  const nth = anniversaryNthLabel(anchorIso, now);

  return (
    <section
      aria-labelledby="hs-days-eyebrow"
      data-component="DaysTogetherCounter"
      data-tone="days"
      className="flex items-center gap-s-3 min-h-[96px] py-s-6 px-s-5"
    >
      <span
        aria-hidden
        className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold-tint text-gold-ink"
      >
        <HeartHandshake size={22} strokeWidth={1.5} />
      </span>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
        <p
          id="hs-days-eyebrow"
          className="text-micro uppercase tracking-wider text-gold-ink font-semibold"
        >
          交往紀念
        </p>
        <p
          className="text-[28px] font-display font-semibold leading-none text-ink-1 tabular-nums"
          data-testid="days-together-value"
        >
          {daysTogetherLabel(displayed)}
        </p>
        <p className="text-meta text-ink-2 truncate" data-testid="days-together-meta">
          {metaLabel || nth}
        </p>
      </div>
    </section>
  );
}
